import { tWithKeyAttr as t } from "../../i18nHelper";
import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import { SocketContext } from "../../SocketContext";
import { useLocation, useNavigate } from "react-router-dom";
import { Category } from "../../types/types";
import useMediaQuery from "../../hooks/useMediaQuery";
import { mobileWidth } from "../../utils/constants";
import VideoCallWindow from "../modals/videoCallWindow/videoCallWindow";
import { useAppSelector } from "../../services/hooks";
import { isAuthSelector } from "../../services/selectors/auth.selectors";
import {
  useGetChatAppointmentsQuery,
  useGetUserQuery,
  useGetUserEntitlementsQuery,
  useLazyGetMainPageInfoQuery,
  useLazyGetPendingRecordingSessionsQuery,
  useTakeInquiryMutation,
} from "../../services/api/user.api";
import { skipToken } from "@reduxjs/toolkit/query";
import Loader from "../loader/loader";
import Modal from "../modals/modal/Modal";
import InactivityWarning from "../modals/inactivityWarning/inactivityWarning";
import InactivityLogoutPopup from "../modals/inactivityLogoutPopup/inactivityLogoutPopup";
import {
  pendingReportAppointmentsSelector,
  pendingReportInquiriesSelector,
  showUserJoinedModalSelector,
  userSelector,
} from "../../services/selectors/user.selectors";
import UserJoinedChatSuccessPopup from "../modals/userJoinedChatSuccessPopup/userJoinedChatSuccessPopup";
import { useActions } from "../../services/hooks/useActions";
import { useRoutes } from "../../hooks/useRoutes";
import MediaDevicesErrorPopup from "../modals/mediaDevicesErrorPopup/mediaDevicesErrorPopup";
import { chatUsersSelector } from "../../services/selectors/chat.selector";
import useInactivityTimer from "../../hooks/useInactivityTimer";
import MediaDevicesNotFoundErrorPopup from "../modals/mediaDevicesNotFoundErrorPopup/mediaDevicesNotFoundErrorPopup";
import PendingReportAppointmentsPopup from "../modals/pendingReportAppointmentsPopup/pendingReportAppointmentsPopup";
// import i18n from "../../i18n";
import {
  // addKeysToAllTranslatedElements,
  enableTranslationMode,
} from "../../translationMode";
// import { isSuperAdmin } from "../../services/selectors/admin.selectors";
// import { useSelector } from "react-redux";
import PrivacyPolicyConsentPopup from "../modals/privacyPolicyConsentPopup/privacyPolicyConsentPopup";
// import { setSuperAdmin } from "../../services/slices/admin.slice";
// import ChangeEditingModeButton from "../changeEditingModeButton/changeEditingModeButton";
// import EditModeNotice from "../EditModeNotice/EditModeNotice";
// import PrivacyPolicyConsentPopup from "../modals/privacyPolicyConsentPopup/privacyPolicyConsentPopup";
import { LanguageSelection } from "../LanguageSelection/LanguageSelection";
import PendingRecordingPopup from "../modals/pendingRecordingPopup/pendingRecordingPopup";
import { therapistIdSelector } from "../../services/selectors/user.selectors";
import { isSessionLive } from "../../utils/logoutLock";
import i18n from "../../i18n";
import GeneralErrorPopup from "../modals/errorPopup/errorPopup";
import {
  aiCreditsDataSelector,
  quotaDataSelector,
  restrictionTypeSelector,
} from "../../services/selectors/accessControl.selectors";
import { IConversation } from "../../services/types";
import {
  callStateSelector,
  currentCallSelector,
  cdrDataSelector,
} from "../../services/selectors/callCenter.selectors";
import { clearCall, setCallActive, rejectIncomingCall } from "../../services/slices/callCenter.slice";
import { InquiryDocumentPanelProvider } from "../../contexts/InquiryDocumentPanelContext";
import IncomingCallContent from "../callCenter/IncomingCallContent/IncomingCallContent";
import ActiveCallWidgetWithCard from "../callCenter/ActiveCallWidget/ActiveCallWidgetWithCard";
import SipStatusIndicator from "../callCenter/SipStatusIndicator/SipStatusIndicator";
import modalBackdropStyles from "../callCenter/CallCenterModalBackdrop.module.css";
import incomingCallSound from "../../sounds/incomingCallSound.wav";
import { usePaycall } from "../../contexts/PaycallContext";
import { useDispatch } from "react-redux";
window.enableTranslationMode = enableTranslationMode;

function App() {
  // const [close, setClose] = useState(false);
  // const isSuperAd = useSelector(isSuperAdmin);
  // const [dataFromL, setDataFromL] = useState<any>(null);
  // const [translationMode, setTranslationMode] = useState<boolean>(dataFromL);
  const { isLoading: isUserLoading } = useGetUserQuery();

  const mobile = useMediaQuery(mobileWidth);
  const isAuth = useAppSelector(isAuthSelector);
  const chatUsers = useAppSelector(chatUsersSelector);
  const therapist = useAppSelector(userSelector);
  const therapistId = useAppSelector(therapistIdSelector);

  const restrictionType = useAppSelector(restrictionTypeSelector);
  const quotaData = useAppSelector(quotaDataSelector);
  const aiCreditsData = useAppSelector(aiCreditsDataSelector);
  const { setEntitlements, clearEntitlements, resetShowUserJoinedModal, hideRestrictionPopup } = useActions();
  const { data: entitlementsData } = useGetUserEntitlementsQuery(
    undefined,
    { skip: !therapistId }
  );
  useEffect(() => {
    if (entitlementsData) setEntitlements(entitlementsData);
  }, [entitlementsData, setEntitlements]);
  useEffect(() => {
    if (!isAuth) clearEntitlements();
  }, [isAuth, clearEntitlements]);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const paycall = usePaycall();

  // Call Center State
  const callState = useAppSelector(callStateSelector);
  const currentCall = useAppSelector(currentCallSelector);
  const cdrData = useAppSelector(cdrDataSelector);
  /** After SignalR ends the call, keep the floating widget briefly for end animation. */
  const [showCallEndedOverlay, setShowCallEndedOverlay] = useState(false);
  const [incomingAnswerError, setIncomingAnswerError] = useState<string | null>(
    null
  );
  const [incomingAnswerLoading, setIncomingAnswerLoading] = useState(false);

  useEffect(() => {
 
    if (
      callState === "ended" &&
      currentCall &&
      cdrData &&
      cdrData.callId === currentCall.callId
    ) {
      
      // Show the ended animation for both inbound and outbound calls
      setShowCallEndedOverlay(true);
      const tid = window.setTimeout(() => {
        setShowCallEndedOverlay(false);
      }, 1500);
      return () => window.clearTimeout(tid);
    }
    
    setShowCallEndedOverlay(false);
    return undefined;
  }, [callState, currentCall?.callId, cdrData?.callId]);

  // --- Pending Recording Recovery ---
  const [pendingSessions, setPendingSessions] = useState<any[]>([]);
  const [showPendingRecording, setShowPendingRecording] = useState(false);
  const [triggerGetPendingSessions] = useLazyGetPendingRecordingSessionsQuery();

  // Track which therapist identity was already checked (not a single boolean,
  // so that switching companies / identities triggers a fresh check).
  const checkedForTherapistRef = useRef<string | null>(null);
  // Session IDs the user already closed the popup on (without dismissing).
  // Using a ref (not sessionStorage) so it resets on page reload — the user
  // always sees active sessions at least once per visit.
  const closedSessionIdsRef = useRef<Set<string>>(new Set());
  const callCenterRingAudioRef = useRef<HTMLAudioElement | null>(null);
  /** Prevents double-submit while Therapist_TakeUserRequest is in flight. */
  const answerCallInFlightRef = useRef(false);
  /** User rejected while take-inquiry was pending — ignore late success. */
  const answerTakeCancelledRef = useRef(false);

  /* Loop ringtone while incoming call overlay is ringing; stop on answer, reject, or state change. */
  useEffect(() => {
    const el = callCenterRingAudioRef.current;
    if (!el) return;

    if (!isAuth || callState !== "ringing" || !currentCall) {
      el.pause();
      el.currentTime = 0;
      return;
    }

    el.loop = true;
    el.volume = 0.38;
    void el.play().catch(() => {
      /* Autoplay policy: may fail until a user gesture; unavoidable in some browsers. */
    });
  }, [isAuth, callState, currentCall?.callId]);

  // Reset on logout so next login triggers a fresh check
  useEffect(() => {
    if (!isAuth) {
      checkedForTherapistRef.current = null;
      closedSessionIdsRef.current = new Set();
    }
  }, [isAuth]);

  // Check for pending sessions once on login / page refresh.
  // Per-patient checks happen in recordMeetingSearch when the user clicks Continue.
  useEffect(() => {
    if (!isAuth || !therapistId) return;

    // Only check once per identity
    if (checkedForTherapistRef.current === String(therapistId)) return;

    checkedForTherapistRef.current = String(therapistId);
    triggerGetPendingSessions(Number(therapistId))
      .unwrap()
      .then((result) => {
        if (result?.success && result.sessions?.length > 0) {
          let sessionsToShow = result.sessions;

          // Filter out sessions the user already closed the popup on
          if (closedSessionIdsRef.current.size > 0) {
            sessionsToShow = sessionsToShow.filter(
              (s: any) => !closedSessionIdsRef.current.has(s.sessionId)
            );
          }

          // Filter out sessions that are actively being recorded in another tab
          sessionsToShow = sessionsToShow.filter(
            (s: any) => !isSessionLive(s.sessionId)
          );

          // Filter out sessions recently finalized (race condition: DB update may lag)
          try {
            const finalized: string[] = JSON.parse(localStorage.getItem('finalizedSessions') || '[]');
            if (finalized.length > 0) {
              sessionsToShow = sessionsToShow.filter(
                (s: any) => !finalized.includes(s.sessionId)
              );
            }
          } catch { /* ignore */ }

          if (sessionsToShow.length > 0) {
            setPendingSessions(sessionsToShow);
            setShowPendingRecording(true);
          }
        }
      })
      .catch(() => {
        // Silently fail - this is a non-critical enhancement
      });
  }, [isAuth, therapistId, triggerGetPendingSessions]);
  // const admin = sessionStorage.getItem("isAdmin") === "true";
  // useEffect(() => {
  //   if(admin || dataL)
  //   {
  //   if (i18n.isInitialized) {
  //     addKeysToAllTranslatedElements();
  //   } else {
  //     i18n.on("initialized", () => {
  //       addKeysToAllTranslatedElements();
  //     });
  //   }
  //   }
  // }, []);
  // useEffect(() => {
  //   setTranslationMode(dataFromL);
  // }, [dataFromL]);
  // const dispatch = useDispatch();

  // useEffect(() => {
  //   if (admin || dataL) {
  //     dispatch(setSuperAdmin({ flag: true }));
  //     setTranslationMode(true);
  //   } else if (isSuperAd && !close) {
  //     setTranslationMode(true);
  //   }
  // }, []);
  // const handleToggle = () => {
  //   if (translationMode) {
  //     setClose(true);
  //     setTranslationMode(false);
  //   } else {
  //     setClose(false);
  //     sessionStorage.setItem("isAdmin", "true");
  //     setTranslationMode(true);
  //   }
  // };

  // useEffect(() => {
  //   if (!hasOpenedModalRef.current) {
  //     setOpenModal(true);
  //     hasOpenedModalRef.current = true;
  //   } else (window as any).enableTranslationMode?.(translationMode);
  // }, [translationMode]);
  //ws
  const showUserJoinedModal = useAppSelector(showUserJoinedModalSelector);

  //video calls
  const {
    startCall,
    setStartCall,
    callAccepted,
    showMediaDevicesErrorPopup,
    setShowMediaDevicesErrorPopup,
    setShowMediaDevicesNotFoundPopup,
    showMediaDevicesNotFoundPopup,
  } = useContext(SocketContext);

  const [showWarning, setShowWarning] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [userJoined, setUserJoined] = useState<null | string>(null);

  //get all appointments
  useGetChatAppointmentsQuery(therapist.guid ? therapist.guid : skipToken);

  const [takeInquiryMutation] = useTakeInquiryMutation();
  const [refetchMainPageInfo] = useLazyGetMainPageInfoQuery();

  useInactivityTimer(setShowWarning, setShowLogoutModal);

  useEffect(() => {
    setIncomingAnswerError(null);
    setIncomingAnswerLoading(false);
    answerTakeCancelledRef.current = false;
  }, [currentCall?.callId]);


  useEffect(() => {
    if (showUserJoinedModal) {
      const user = chatUsers.find(
        (u: IConversation) => String(u.userId) === String(showUserJoinedModal)
      );
      user && user.appointment?.active && setUserJoined(user.fullName);
    }
  }, [showUserJoinedModal, chatUsers]);

  // show pending report appointments popup
  const pendingReportAppointments = useAppSelector(
    pendingReportAppointmentsSelector
  );
  const pendingReportInquiries = useAppSelector(pendingReportInquiriesSelector);
  const hasOpenedModalRef = useRef(false);
  const [openModal, setOpenModal] = useState(false);
  const [dataL, setDataL] = useState(false);

  useEffect(() => {
    const hasPending =
      pendingReportAppointments.length > 0 || pendingReportInquiries.length > 0;

    if (hasPending && !hasOpenedModalRef.current) {
      setOpenModal(true);
      hasOpenedModalRef.current = true; // remember for this session
    }
  }, [pendingReportAppointments, pendingReportInquiries]);
  // useEffect(() => {
  //   const dir = i18n.language === "he"|| i18n.language === "ar" ? "rtl" : "ltr";
  //   document.documentElement.setAttribute("dir", dir);
  //   document.documentElement.setAttribute("lang", i18n.language);
  // }, [i18n.language]);

  //menu
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [activeCategory, setActiveCategory] = useState(Category.Main);
  const changeCategory = (e: React.MouseEvent, cat: Category) => {
    if (!mobile) {
      e?.stopPropagation();
    }
    setActiveCategory(cat);
  };

  // GMT
  const [showGMTPopup, setShowGMTPopup] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("privacyConsent");
    if (!consent) {
      setShowGMTPopup(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem("privacyConsent", "true");
    setShowGMTPopup(false);
  };

  // Call Center Handlers — incoming modal only (Therapist_TakeUserRequest / takeInquiry).
  const handleAnswerCall = useCallback(async () => {
    if (!currentCall || answerCallInFlightRef.current || incomingAnswerLoading) {
      return;
    }

    setIncomingAnswerError(null);
    answerTakeCancelledRef.current = false;

    const proceedWithAnswer = () => {
      if (location.pathname !== "/main") {
        navigate("/main");
      }
      if (callState === "ringing") {
        dispatch(setCallActive());
      }
    };

    try {
      await paycall.answerSipCall();
    } catch {
      setIncomingAnswerError(t("CC_TAKE_INQUIRY_FAILED"));
      return;
    }

    // Unidentified caller: no inquiry payload — answer without backend take.
    if (!currentCall.isIdentified || !currentCall.callerInfo) {
      proceedWithAnswer();
      return;
    }

    const ci = currentCall.callerInfo;
    const therapistsGuid =
      typeof therapist?.guid === "string" ? therapist.guid.trim() : "";
    const usersGuid = ci.guid?.trim() ?? "";
    const questionnaireId = ci.questionnaireId;
    const questionnaireCompaniesId = ci.questionnaireCompaniesId;
    const cycle = ci.cycle ?? ci.currentCycle;

    const missingCoreFields =
      usersGuid.length === 0 ||
      therapistsGuid.length === 0 ||
      questionnaireId == null ||
      questionnaireCompaniesId == null;

    if (missingCoreFields) {
      setIncomingAnswerError(t("CC_TAKE_INQUIRY_MISSING_FIELDS"));
      return;
    }

    answerCallInFlightRef.current = true;
    setIncomingAnswerLoading(true);
    try {
      // `.unwrap()` resolves when the mutation succeeds (HTTP 2xx per baseQuery).
      // On error response or network failure it rejects — catch below.
      // `Id` matches document flow when therapistsUsersId is unknown (`?? 0`).
      await takeInquiryMutation({
        Id: 0,
        UsersGuid: usersGuid,
        TherapistsGuid: therapistsGuid,
        QuestionnaireId: questionnaireId,
        Cycle: cycle,
        Status: 2,
        QuestionnaireCompaniesId: questionnaireCompaniesId,
      }).unwrap();
      void refetchMainPageInfo();
      if (!answerTakeCancelledRef.current) {
        proceedWithAnswer();
      }
    } catch (err: unknown) {
      if (answerTakeCancelledRef.current) {
        return;
      }
      let message = t("CC_TAKE_INQUIRY_FAILED");
      if (err && typeof err === "object") {
        const r = err as Record<string, unknown>;
        const data = r.data;
        if (typeof data === "string" && data.trim()) {
          message = data;
        } else if (data && typeof data === "object") {
          const d = data as Record<string, unknown>;
          if (typeof d.message === "string") message = d.message;
          else if (typeof d.title === "string") message = d.title;
        } else if (typeof r.message === "string" && r.message.trim()) {
          message = r.message;
        }
      }
      setIncomingAnswerError(message);
    } finally {
      answerCallInFlightRef.current = false;
      setIncomingAnswerLoading(false);
    }
  }, [
    currentCall,
    therapist,
    dispatch,
    callState,
    location.pathname,
    navigate,
    takeInquiryMutation,
    refetchMainPageInfo,
    incomingAnswerLoading,
    paycall,
    t,
  ]);

  const handleRejectCall = () => {
    answerTakeCancelledRef.current = true;
    setIncomingAnswerError(null);
    setIncomingAnswerLoading(false);
    void paycall.rejectSipCall();
    if (currentCall?.callId) {
      dispatch(rejectIncomingCall(currentCall.callId));
    } else {
      dispatch(clearCall());
    }
  };

  const routes = useRoutes(isAuth, dataL, changeCategory);

  // Local date of the reset, when the server sent one (resetsAt is UTC).
  const buildAiCreditsText = () => {
    const resetsAt = aiCreditsData?.resetsAt
      ? new Date(aiCreditsData.resetsAt)
      : null;
    const resetDate =
      resetsAt && !isNaN(resetsAt.getTime())
        ? resetsAt.toLocaleDateString(i18n.language, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
        : "";

    return [
      t("AI_CREDITS_EXHAUSTED_ERROR", {
        defaultValue:
          "יתרת קרדיטי ה-AI נוצלה במלואה, לא ניתן להשתמש בשירותי ה-AI כעת.",
      }),
      resetDate &&
        t("AI_CREDITS_EXHAUSTED_RESETS_AT", {
          date: resetDate,
          defaultValue: `היתרה תתחדש בתאריך ${resetDate}.`,
        }),
    ]
      .filter(Boolean)
      .join(" ");
  };

  useEffect(() => {
    setDataL(dataL);
  }, [dataL]);

  return (
    <InquiryDocumentPanelProvider>
    <>
      {isAuth && (
        <audio
          ref={callCenterRingAudioRef}
          src={incomingCallSound}
          preload="auto"
          playsInline
          style={{
            position: "fixed",
            width: 0,
            height: 0,
            opacity: 0,
            pointerEvents: "none",
          }}
          aria-hidden
        />
      )}
      <LanguageSelection />
      {showGMTPopup && (
        <Modal onClose={() => { }}>
          <PrivacyPolicyConsentPopup handleAccept={handleAccept} />
        </Modal>
      )}

      {/*{isSuperAd && (*/}
      {/*  <ChangeEditingModeButton*/}
      {/*    translationMode={translationMode}*/}
      {/*    onToggle={handleToggle}*/}
      {/*  />*/}
      {/*)}*/}
      {/*{translationMode && <EditModeNotice />}*/}

      {isAuth && (startCall || callAccepted) && (
        <VideoCallWindow onClose={() => { setStartCall(false) }} />
      )}
      {isAuth && showWarning && (
        <Modal onClose={() => setShowWarning(false)}>
          <InactivityWarning onClose={() => setShowWarning(false)} />
        </Modal>
      )}
      {isAuth && showMediaDevicesErrorPopup && (
        <Modal onClose={() => setShowMediaDevicesErrorPopup(false)}>
          <MediaDevicesErrorPopup
            onClose={() => setShowMediaDevicesErrorPopup(false)}
          />
        </Modal>
      )}
      {isAuth && showMediaDevicesNotFoundPopup && (
        <Modal onClose={() => setShowMediaDevicesNotFoundPopup(false)}>
          <MediaDevicesNotFoundErrorPopup
            onClose={() => setShowMediaDevicesNotFoundPopup(false)}
          />
        </Modal>
      )}
      {showLogoutModal && (
        <Modal onClose={() => setShowLogoutModal(false)}>
          <InactivityLogoutPopup onClose={() => setShowLogoutModal(false)} />
        </Modal>
      )}
      {isAuth && userJoined && (
        <Modal
          onClose={() => {
            setUserJoined(null);
            resetShowUserJoinedModal();
          }}
        >
          <UserJoinedChatSuccessPopup
            userName={userJoined}
            onClose={() => {
              setUserJoined(null);
              resetShowUserJoinedModal();
            }}
          />
        </Modal>
      )}
      {isAuth && openModal && (
        <Modal
          onClose={() => {
            setOpenModal(false);
          }}
        >
          <PendingReportAppointmentsPopup
            onClose={() => {
              setOpenModal(false);
            }}
          />
        </Modal>
      )}

      {/* Pending Recording Recovery Modal */}
      {isAuth && showPendingRecording && pendingSessions.length > 0 && (
        <Modal onClose={() => {
          pendingSessions.forEach((s: any) => closedSessionIdsRef.current.add(s.sessionId));
          setShowPendingRecording(false);
        }}>
          <PendingRecordingPopup
            sessions={pendingSessions}
            onClose={() => {
              pendingSessions.forEach((s: any) => closedSessionIdsRef.current.add(s.sessionId));
              setShowPendingRecording(false);
            }}
            onDismissed={(sessionId) => {
              const remaining = pendingSessions.filter((s: any) => s.sessionId !== sessionId);
              setPendingSessions(remaining);
              if (remaining.length === 0) setShowPendingRecording(false);
            }}
            onFinalized={(sessionId, userId, recordNumber, patientGuid, patientName) => {
              const remaining = pendingSessions.filter((s: any) => s.sessionId !== sessionId);
              setPendingSessions(remaining);
              if (remaining.length === 0) setShowPendingRecording(false);
              navigate(`/users/recordTable`, {
                state: {
                  Guid: patientGuid,
                  UsersId: userId,
                  patientName: patientName,
                  newRecordNumber: recordNumber,
                },
              });
            }}
            onContinueRecording={(session) => {
              setShowPendingRecording(false);
              setPendingSessions([]);
              navigate("/users/recording", {
                state: {
                  Guid: session.patientGuid,
                  UsersId: session.userId,
                  patientName: session.patientName,
                  appointmentId: session.appointmentId ?? undefined,
                  appointmentStartTime: session.appointmentStartTime ?? undefined,
                  resumeSession: {
                    sessionId: session.sessionId,
                    chunkCount: session.chunkCount,
                    mimeType: session.mimeType,
                    durationSeconds: session.recordingDurationSeconds || 0,
                  },
                },
              });
            }}
          />
        </Modal>
      )}

      {isAuth && (restrictionType === "feature" || restrictionType === "resource") && (
        <Modal onClose={hideRestrictionPopup}>
          <GeneralErrorPopup
            onClose={hideRestrictionPopup}
            text={t(restrictionType === "feature" ? "FEATURE_NOT_IN_PLAN_ERROR" : "RESOURCE_QUOTA_EXCEEDED_ERROR")} />
        </Modal>
      )}
      {isAuth && restrictionType === "feature_quota" && quotaData && (
        <Modal onClose={hideRestrictionPopup}>
          <GeneralErrorPopup
            onClose={hideRestrictionPopup}
            text={
              t("FEATURE_QUOTA_EXCEEDED_ERROR")
            }
          />
        </Modal>
      )}
      {isAuth && restrictionType === "ai_credits" && (
        <Modal onClose={hideRestrictionPopup}>
          <GeneralErrorPopup
            onClose={hideRestrictionPopup}
            text={buildAiCreditsText()}
          />
        </Modal>
      )}

      {/* Call Center UI */}
      {isAuth && callState === "ringing" && currentCall && !currentCall.isOutbound && (
        <div className={modalBackdropStyles.backdrop}>
          <IncomingCallContent
            call={currentCall}
            onAnswer={handleAnswerCall}
            onReject={handleRejectCall}
            answerError={incomingAnswerError}
            answerLoading={incomingAnswerLoading}
            onDismissAnswerError={() => setIncomingAnswerError(null)}
          />
        </div>
      )}

      {/* Call Center UI - Active Call Widget */}
      {(() => {
        const shouldRenderWidget = isAuth &&
          currentCall &&
          (callState === "active" ||
            (callState === "ended" &&
              showCallEndedOverlay &&
              cdrData));       

        return shouldRenderWidget ? (
          <ActiveCallWidgetWithCard
            isRemoteEndedPhase={
              callState === "ended" && showCallEndedOverlay
            }
          />
        ) : null;
      })()}

      {/* Warns when the softphone is not registered — incoming calls are suppressed then. */}
      {isAuth && <SipStatusIndicator />}

      {isUserLoading ? <Loader /> : routes}
    </>
    </InquiryDocumentPanelProvider>
  );
}

export default App;

declare global {
  interface Window {
    enableTranslationMode: (isEnabled: boolean) => void;
  }
}
